import { useWatchingOver } from '@/hooks/use-watching-over.hook';
import {
  countUnseenByFriendship,
  groupByFriendship,
  prunedSeenKeys,
  seenWatermark,
  toCircleAlertItems
} from '@/lib/circle-alerts';
import {
  getSeenAlerts,
  getServerSeenAlerts,
  setSeenAlerts,
  subscribeSeenAlerts
} from '@/lib/circle-alerts-seen-store';
import { fraudFrontQueryClient, queryClient } from '@/lib/react-query';
import type { CircleConcernsData } from '@/types/iris-v2';
import { useCallback, useMemo, useSyncExternalStore } from 'react';

const CONCERNS_PATH = '/api/v1/friends/circle/concerns';

// The endpoint defaults to 7 days; 30 is its maximum. Seen alerts stay listed
// for the whole window — being seen only clears the badges.
const ALERT_WINDOW_DAYS = 30;

// Prefix of the openapi-react-query key (['get', path, init]) — matches the
// concerns query regardless of its params.
const CONCERNS_QUERY_KEY = ['get', CONCERNS_PATH];

/** Refetch circle alerts now, e.g. when a CONCERN_ALERT push arrives. */
export const invalidateCircleAlerts = () =>
  queryClient.invalidateQueries({ queryKey: CONCERNS_QUERY_KEY });

const EMPTY_ITEMS: never[] = [];

/**
 * Alerts about the people the user watches over, for the header bell and the
 * per-person sections on the dashboard.
 *
 * The concerns endpoint returns the whole (mutual) circle, so it's filtered to
 * the watched-over friendships. It also runs several queries per circle
 * member, so the backend asked us to fetch it on load and tab focus only —
 * never on an interval — and it isn't fetched at all when the user watches
 * over nobody. `staleTime` keeps rapid tab switching from refetching.
 */
export function useCircleAlerts({
  enabled = true
}: {
  enabled?: boolean;
} = {}) {
  const { data: profile } = fraudFrontQueryClient.useQuery(
    'get',
    '/api/v1/customers/profile',
    {},
    { enabled }
  );
  const customerId = profile?.customer?.id;
  const locallySeen = useSyncExternalStore(
    subscribeSeenAlerts,
    () => getSeenAlerts(customerId),
    getServerSeenAlerts
  );

  const watchingOver = useWatchingOver({ enabled });
  const watchedFriendshipIds = useMemo(
    () =>
      new Set(
        (watchingOver.data?.content ?? []).flatMap((friendship) =>
          friendship.id === undefined ? [] : [friendship.id]
        )
      ),
    [watchingOver.data]
  );

  const concerns = fraudFrontQueryClient.useQuery(
    'get',
    CONCERNS_PATH,
    { params: { query: { days: ALERT_WINDOW_DAYS } } },
    {
      enabled: enabled && watchedFriendshipIds.size > 0,
      staleTime: 30_000,
      refetchOnWindowFocus: true
    }
  );
  const lastSeenAt = concerns.data?.lastSeenAt;

  const items = useMemo(
    () => toCircleAlertItems(concerns.data, watchedFriendshipIds, locallySeen),
    [concerns.data, watchedFriendshipIds, locallySeen]
  );
  const itemsByFriendship = useMemo(() => groupByFriendship(items), [items]);
  const unseenCountByFriendship = useMemo(
    () => countUnseenByFriendship(items),
    [items]
  );

  const { mutate: markSeenOnServer } = fraudFrontQueryClient.useMutation(
    'post',
    '/api/v1/friends/circle/concerns/seen',
    {
      onSuccess: (_data, variables) => {
        const asOf = variables.body?.asOf;
        if (!asOf) {
          return;
        }
        queryClient.setQueriesData<CircleConcernsData>(
          { queryKey: CONCERNS_QUERY_KEY },
          (old) => (old ? { ...old, lastSeenAt: asOf } : old)
        );
      }
    }
  );

  // Stable until the data changes, so callers can use them in hook deps.
  const alertsFor = useCallback(
    (friendshipId: number | undefined) =>
      (friendshipId !== undefined && itemsByFriendship.get(friendshipId)) ||
      EMPTY_ITEMS,
    [itemsByFriendship]
  );
  const unseenCountFor = useCallback(
    (friendshipId: number | undefined) =>
      friendshipId === undefined
        ? 0
        : (unseenCountByFriendship.get(friendshipId) ?? 0),
    [unseenCountByFriendship]
  );

  /**
   * Marks one person's alerts seen: clears their badge at once via the local
   * set, then moves the server's `lastSeenAt` as far as it can go without
   * clearing anyone else's unopened alerts.
   */
  const markPersonSeen = useCallback(
    (friendshipId: number) => {
      if (customerId === undefined) {
        return;
      }
      const opened = items.filter(
        (item) => item.friendshipId === friendshipId && item.isNew
      );
      if (opened.length === 0) {
        return;
      }
      const seen = new Set([...locallySeen, ...opened.map((i) => i.key)]);
      setSeenAlerts(customerId, prunedSeenKeys(items, seen, lastSeenAt));

      const afterOpening = items.map((item) =>
        seen.has(item.key) ? { ...item, isNew: false } : item
      );
      const asOf = seenWatermark(afterOpening, lastSeenAt);
      if (asOf) {
        markSeenOnServer(
          { body: { asOf } },
          {
            onSuccess: () =>
              setSeenAlerts(
                customerId,
                prunedSeenKeys(items, getSeenAlerts(customerId), asOf)
              )
          }
        );
      }
    },
    [customerId, items, locallySeen, lastSeenAt, markSeenOnServer]
  );

  return {
    items,
    isLoading: watchingOver.isLoading || concerns.isLoading,
    unseenCount: items.filter((i) => i.isNew).length,
    unseenPeopleCount: unseenCountByFriendship.size,
    alertsFor,
    unseenCountFor,
    markPersonSeen
  };
}
